"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Chip_1 = require("./Chip");
const { root, filled, outlined, inverse, disabled, clickable, colorPrimary, deleteIcon, focusVisible } = Chip_1.chipClasses;
const MuiChipOverrides = {
    MuiChip: {
        styleOverrides: {
            root: ({ theme }) => ({
                [`&.${root}.MuiChip-colorDefault.${filled}`]: {
                    backgroundColor: theme.palette.gray[200],
                    color: theme.palette.gray[900],
                    fontWeight: 400,
                    [`&:hover`]: {
                        backgroundColor: theme.palette.gray[400],
                    },
                    [`&.${disabled}`]: {
                        backgroundColor: theme.palette.gray[300],
                        color: theme.palette.gray[500],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.gray[700],
                    },
                },
                [`&.${root}.MuiChip-colorDefault.${outlined}`]: {
                    backgroundColor: theme.palette.common.white,
                    color: theme.palette.gray[900],
                    fontWeight: 400,
                    borderColor: theme.palette.gray[500],
                    [`&:hover`]: {
                        backgroundColor: theme.palette.gray[200],
                    },
                    [`&.${disabled}`]: {
                        backgroundColor: theme.palette.common.white,
                        color: theme.palette.gray[500],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.gray[700],
                    },
                },
                [`&.${root}.${outlined}:not(&:hover):not(.${focusVisible})`]: {
                    backgroundColor: theme.palette.common.white,
                },
                [`&.${root}.${colorPrimary}.${outlined}`]: {
                    borderColor: theme.palette.blue[500],
                },
                [`&.${root}.${outlined}.${focusVisible}`]: {
                    boxShadow: theme.shadows[1],
                },
                [`&.${root}.${inverse}.${focusVisible}`]: {
                    boxShadow: theme.shadows[1],
                },
            }),
        },
        variants: [
            // Filled
            {
                props: { variant: 'filled', color: 'primary' },
                style: ({ theme }) => ({
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.blue[100],
                        '&:hover': {
                            color: theme.palette.blue[100],
                        },
                    },
                }),
            },
            {
                props: { variant: 'filled', color: 'warning' },
                style: ({ theme }) => ({
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.yellow[100],
                        '&:hover': {
                            color: theme.palette.yellow[100],
                        },
                    },
                }),
            },
            {
                props: { variant: 'filled', color: 'error' },
                style: ({ theme }) => ({
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.red[100],
                        '&:hover': {
                            color: theme.palette.red[100],
                        },
                    },
                }),
            },
            {
                props: { variant: 'filled', color: 'success' },
                style: ({ theme }) => ({
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.green[100],
                        '&:hover': {
                            color: theme.palette.green[100],
                        },
                    },
                }),
            },
            // Outlined
            {
                props: { variant: 'outlined', color: 'default' },
                style: ({ theme }) => ({
                    [`&.${root}.${outlined}.${focusVisible}`]: {
                        backgroundColor: theme.palette.gray[400],
                    },
                }),
            },
            {
                props: { variant: 'outlined', color: 'primary' },
                style: ({ theme }) => ({
                    color: theme.palette.blue[500],
                    border: `1px solid ${theme.palette.blue[500]}`,
                    [`&.${clickable}:hover`]: {
                        backgroundColor: theme.palette.blue[100],
                    },
                    [`&.${root}.${focusVisible}`]: {
                        backgroundColor: theme.palette.blue[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.blue[500],
                        '&:hover': {
                            color: theme.palette.blue[500],
                        },
                    },
                }),
            },
            {
                props: { variant: 'outlined', color: 'warning' },
                style: ({ theme }) => ({
                    color: theme.palette.yellow[500],
                    border: `1px solid ${theme.palette.yellow[500]}`,
                    [`&.${clickable}:hover`]: {
                        backgroundColor: theme.palette.yellow[100],
                    },
                    [`&.${root}.${focusVisible}`]: {
                        backgroundColor: theme.palette.yellow[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.yellow[500],
                        '&:hover': {
                            color: theme.palette.yellow[500],
                        },
                    },
                }),
            },
            {
                props: { variant: 'outlined', color: 'error' },
                style: ({ theme }) => ({
                    color: theme.palette.red[500],
                    border: `1px solid ${theme.palette.red[500]}`,
                    [`&.${clickable}:hover`]: {
                        backgroundColor: theme.palette.red[100],
                    },
                    [`&.${root}.${focusVisible}`]: {
                        backgroundColor: theme.palette.red[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.red[500],
                        '&:hover': {
                            color: theme.palette.red[500],
                        },
                    },
                }),
            },
            {
                props: { variant: 'outlined', color: 'success' },
                style: ({ theme }) => ({
                    color: theme.palette.green[500],
                    border: `1px solid ${theme.palette.green[500]}`,
                    [`&.${clickable}:hover`]: {
                        backgroundColor: theme.palette.green[100],
                    },
                    [`&.${root}.${focusVisible}`]: {
                        backgroundColor: theme.palette.green[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.green[500],
                        '&:hover': {
                            color: theme.palette.green[500],
                        },
                    },
                }),
            },
            // Inverse
            {
                props: { variant: 'inverse', color: 'default' },
                style: ({ theme }) => ({
                    backgroundColor: theme.palette.gray[200],
                    color: theme.palette.gray[900],
                    '&:hover': {
                        backgroundColor: theme.palette.gray[200],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.gray[700],
                        '&:hover': {
                            color: theme.palette.gray[700],
                        },
                    },
                    [`&.${focusVisible}`]: {
                        backgroundColor: theme.palette.gray[400],
                    },
                }),
            },
            {
                props: { variant: 'inverse', color: 'primary' },
                style: ({ theme }) => ({
                    backgroundColor: theme.palette.blue[100],
                    color: theme.palette.blue[500],
                    '&:hover': {
                        backgroundColor: theme.palette.blue[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.blue[500],
                        '&:hover': {
                            color: theme.palette.blue[500],
                        },
                    },
                    [`&.${focusVisible}`]: {
                        backgroundColor: theme.palette.blue[100],
                    },
                }),
            },
            {
                props: { variant: 'inverse', color: 'warning' },
                style: ({ theme }) => ({
                    backgroundColor: theme.palette.yellow[100],
                    color: theme.palette.yellow[500],
                    '&:hover': {
                        backgroundColor: theme.palette.yellow[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.yellow[500],
                        '&:hover': {
                            color: theme.palette.yellow[500],
                        },
                    },
                    [`&.${focusVisible}`]: {
                        backgroundColor: theme.palette.yellow[100],
                    },
                }),
            },
            {
                props: { variant: 'inverse', color: 'error' },
                style: ({ theme }) => ({
                    backgroundColor: theme.palette.red[100],
                    color: theme.palette.red[500],
                    '&:hover': {
                        backgroundColor: theme.palette.red[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.red[500],
                        '&:hover': {
                            color: theme.palette.red[500],
                        },
                    },
                    [`&.${focusVisible}`]: {
                        backgroundColor: theme.palette.red[100],
                    },
                }),
            },
            {
                props: { variant: 'inverse', color: 'success' },
                style: ({ theme }) => ({
                    backgroundColor: theme.palette.green[100],
                    color: theme.palette.green[500],
                    '&:hover': {
                        backgroundColor: theme.palette.green[100],
                    },
                    [`& .${deleteIcon}`]: {
                        color: theme.palette.green[500],
                        '&:hover': {
                            color: theme.palette.green[500],
                        },
                    },
                    [`&.${focusVisible}`]: {
                        backgroundColor: theme.palette.green[100],
                    },
                }),
            },
        ],
    },
};
exports.default = MuiChipOverrides;
//# sourceMappingURL=MuiChipOverrides.js.map